import { useState, useRef, useContext, useEffect, useCallback } from "react";
import "../../Styles/resizable.css";

import { CANCEL_INCIDENT } from "../../Mutations/CancelIncident";

import { useMutation } from "@apollo/client";
import { SET_STATUS } from "../../Mutations/SetStatus";
import { SET_LAST_CONTACTED } from "../../Mutations/SetLastContacted";
import { SET_OFFICER_INCIDENT_TIMES } from "../../Mutations/SetOfficerIncidentTimes";
import { SET_AVAIL_OFFICER } from "../../Mutations/SetAvailOfficer";
import { SET_SECONDARY_LOCATION } from "../../Mutations/SetSecondaryLocation";

import {
  numberSort,
  wordSort,
  dateSort,
  incidentNumberSort,
  useLocalStorage,
  updateOfficerTimesInOis,
  localGetData,
  localStoreData,
  parseDateFlexible,
} from "../../utils/utils";
import useWindowSize from "../../utils/window-size";

import { ResizableBox } from "react-resizable";

import DragHandleIcon from "@material-ui/icons/DragHandle";
import { Table, TableContainer, Paper, Box } from "@material-ui/core";

import TableHeaders from "../TableHeaders";
import ActiveIncidentTableRows from "../ActiveIncidentTableRow/ActiveIncidentTableRows";
import TableColumnShowHide from "../TableColumnShowHide";
import { DashboardContext } from "../../Contexts/DashboardContext";
import { ACTIVE_INCIDENTS_ACTIONS } from "../../Actions/ActiveIncidentsActions";
import { useCookies } from "react-cookie";
import { UserContext } from "../../Contexts/UserContext";
import { useStyles } from "../../Styles/styles";
import { canEditAvailableOfficers, isSuperUser } from "../../utils/Roles";
import {
  incidentAlertColor,
  parseAddressNameForDashboard,
} from "../../utils/utils";
import { checkPermission } from "../../utils/Roles";
import HelpIconButton from "../HelpIconButton";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { AVAILABLE_OFFICER_ACTIONS } from "../../Actions/AvailableOfficerActions";

import moment from "moment";

const SORT = {
  incidentNumber: {
    path: "incidentNumber",
    fnc: incidentNumberSort,
  },
  badge: {
    path: "officerIncidentSpecifics[0].officer.deptAttribute.badge",
    fnc: numberSort,
  },
  assignment: {
    path: "officerIncidentSpecifics[0].officer.deptAttribute.assignment",
    fnc: wordSort,
  },
  type: {
    path: "classification.name",
    fnc: wordSort,
  },
  location: {
    path: "address.name",
    fnc: wordSort,
  },
  secondaryAddress: {
    path: "officerIncidentSpecifics[0].secondaryAddress",
    fnc: wordSort,
  },
  status: {
    path: "officerIncidentSpecifics[0].officer.deptAttribute.status",
    fnc: wordSort,
  },
  onSceneAt: {
    path: "officerIncidentSpecifics[0].onSceneAt",
    fnc: dateSort,
  },
  officerTimer: {
    path: "officerIncidentSpecifics[0].officer.deptAttribute.lastContactedAt",
    fnc: dateSort,
  },
  callTimer: {
    path: "callTime",
    fnc: dateSort,
  },
  carNumber: {
    path: "officerIncidentSpecifics[0].officer.deptAttribute.carNumber",
    fnc: numberSort,
  },
  radioNumber: {
    path: "officerIncidentSpecifics[0].officer.deptAttribute.radioNumber",
    fnc: numberSort,
  },
};

const TABLE_NAME = "activeIncident";

const ActiveIncidentTableContainer = ({
  dashSize,
  logCodes,
  loading,
  error,
  handleIncidentOpening,
  activeIncidentsCalled,
  userPreferences,
}) => {
  const { activeIncidents, activeIncidentDispatch, availableOfficerDispatch } =
    useContext(DashboardContext);
  const { user, onlineState } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const classes = useStyles();

  const { UPDATE_OIS, UPDATE_DEPT_ATTRB } = ACTIVE_INCIDENTS_ACTIONS;
  const { UPDATE_AVAILABLE_OFFICER } = AVAILABLE_OFFICER_ACTIONS;

  const initialColumnView = {
    incidentNumber: true,
    badge: true,
    assignment: true,
    type: true,
    location: true,
    officerStatus: true,
    secondaryAddress: true,
    status: true,
    onSceneAt: true,
    officerTimer: true,
    callTimer: true,
    carNumber: true,
    radioNumber: true,
  };

  const [cancelIncident] = useMutation(CANCEL_INCIDENT);

  const cancelAllActiveIncidents = () => {
    if (
      window.confirm("Are you sure you want to cancel all active incidents?")
    ) {
      (activeIncidents || []).forEach(ai => {
        cancelIncident({
          variables: {
            input: { incidentId: ai.id, originatorId: user?.id, cancel: true },
            context: {
              serializationKey: "incidentQueue",
            },
          },
        });
      });
    }
  };
  const [showColumn, setShowColumn] = useState(initialColumnView);
  const [selectedCell, setSelectedCell] = useState("");
  const [specificRowId, setSpecificRowId] = useState("");

  const [sortDirection, setSortDirection] = useState(
    localGetData(`${user?.id}-table-sorting`)?.[TABLE_NAME]?.direction || null
  );
  const [sortedColumn, setSortedColumn] = useState(
    localGetData(`${user?.id}-table-sorting`)?.[TABLE_NAME]?.sortedColumn ||
      null
  );
  const [sortedId, setSortedId] = useState(
    localGetData(`${user?.id}-table-sorting`)?.[TABLE_NAME]?.id || null
  );

  const [rows, setRows] = useState([]);

  const parseActiveIncidents = useCallback(
    arr => {
      const now = moment();
      const filteredArr = (Array.isArray(arr) ? arr : []).filter(incident => {
        if (!incident?.callTime) return true;
        const parsedCallTime = parseDateFlexible(incident?.callTime);
        return parsedCallTime && parsedCallTime.isBefore(now);
      });
      let nonSelfRows = [];
      let selfRows = [];
      if (filteredArr.length > 0) {
        //Remove ducplicate incidents just in case
        const ids = filteredArr.map(o => o.id);
        const filtered = filteredArr.filter(
          ({ id }, index) => !ids.includes(id, index + 1)
        );
        filtered.map((incident) => {
          const {
            id: incidentId,
            incidentNumber,
            officerIncidentSpecifics,
            classification,
            address,
            callTime,
            sealed,
            locked,
          } = incident;
          const canViewLocked = checkPermission(
            user?.roleNames,
            "view_locked_incident",
            "ALL",
            "",
            incidentId
          );
          const canViewSealed = checkPermission(
            user?.roleNames,
            "view_sealed_incident",
            "ALL",
            "",
            incidentId
          );
          const disabled =
            (!canViewLocked && locked) || (!canViewSealed && sealed)
              ? true
              : false;
          const callTypeColor = incidentAlertColor(
            incident,
            true,
            false,
            false
          );
          const locationColor = incidentAlertColor(
            incident,
            false,
            true,
            false
          );
          const clearedOfficersRemoved =
            officerIncidentSpecifics?.filter(ois => !ois?.clearedAt) || [];
          const incidentOriId = incident?.ori?.id;
          //If the ois has the user, the user is moved to the front of the array to show them while the incident is collapsed
          const userIndex = clearedOfficersRemoved?.findIndex(
            ois => ois?.officer?.id === user?.id.toString()
          );
          if (userIndex >= 0) {
            let officer = clearedOfficersRemoved.splice(userIndex, 1);
            clearedOfficersRemoved.unshift(officer[0]);
          }

          let numOfObjs = clearedOfficersRemoved.length;
          for (let i = 0; i < numOfObjs; i++) {
            let badge,
              assignment,
              radioNumber,
              carNumber,
              officerId,
              lastContactedAt,
              deptAttrId,
              officerStatus,
              loanerOriAbbreviations,
              ori,
              officer;
            if (clearedOfficersRemoved[i].officer) {
              ({
                badge,
                assignment,
                radioNumber,
                carNumber,
                lastContactedAt,
                id: deptAttrId,
                loanerOriAbbreviations,
                ori,
              } = clearedOfficersRemoved[i].officer.deptAttribute);
              // ({ badge, assignment, radioNumber, carNumber, lastContactedAt, id: deptAttrId, status: officerStatus, loanerOriAbbreviations, ori } = clearedOfficersRemoved[i].officer.deptAttribute);
              ({ id: officerId } = clearedOfficersRemoved[i].officer);
              officer = clearedOfficersRemoved[i].officer;
            }
            let {
              id,
              secondaryAddress,
              currentOfficerStatus,
              statusId,
              logCodeId,
              logCode,
              onSceneAt,
              primaryOfficer,
            } = clearedOfficersRemoved[i];
            let ois = clearedOfficersRemoved[i];
            // officerStatus = ois?.logCode?.name?.split("/")?.[0];
            officerStatus = ois?.officer?.deptAttribute?.status;
            let type = classification ? classification.name : null;
            // let location = address ? address.name : null
            let location = address
              ? parseAddressNameForDashboard(address)
              : null;
            let child = i > 0 ? true : false;
            let parent = numOfObjs > 1 && !child ? true : false;
            if (userIndex >= 0) {
              selfRows.push(
                createData(
                  id,
                  incidentNumber,
                  badge,
                  assignment,
                  loanerOriAbbreviations,
                  type,
                  location,
                  secondaryAddress,
                  currentOfficerStatus,
                  statusId,
                  carNumber,
                  radioNumber,
                  officerId,
                  incidentId,
                  logCodeId,
                  logCode,
                  officerStatus,
                  onSceneAt,
                  lastContactedAt,
                  callTime,
                  deptAttrId,
                  ori,
                  child,
                  parent,
                  callTypeColor,
                  locationColor,
                  primaryOfficer,
                  disabled,
                  ois,
                  officer,
                  incidentOriId
                )
              );
            } else {
              nonSelfRows.push(
                createData(
                  id,
                  incidentNumber,
                  badge,
                  assignment,
                  loanerOriAbbreviations,
                  type,
                  location,
                  secondaryAddress,
                  currentOfficerStatus,
                  statusId,
                  carNumber,
                  radioNumber,
                  officerId,
                  incidentId,
                  logCodeId,
                  logCode,
                  officerStatus,
                  onSceneAt,
                  lastContactedAt,
                  callTime,
                  deptAttrId,
                  ori,
                  child,
                  parent,
                  callTypeColor,
                  locationColor,
                  primaryOfficer,
                  disabled,
                  ois,
                  officer,
                  incidentOriId
                )
              );
            }
          }
          return null;
        });
        const allRows = selfRows.concat(nonSelfRows);
        return allRows;
      } else {
        return [];
      }
    },
    [user?.id, user?.roleNames]
  );

  const sortRows = useCallback(
    (direction, id) => {
      let data = activeIncidents;
      if (SORT[id]) {
        setRows(
          parseActiveIncidents(SORT[id].fnc(direction, id, data, SORT[id].path))
        );
      } else {
        setRows(
          parseActiveIncidents(oriOnTop(activeIncidents, user?.homeOriId))
        );
      }
    },
    [activeIncidents, parseActiveIncidents, user?.homeOriId]
  );

  useEffect(() => {
    let data = localGetData(`${user?.id}-active-incident-table-column-view`);
    if (data) {
      setShowColumn(data);
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeIncidents && !loading && !error) {
      sortRows(sortDirection, sortedId);
    }
  }, [activeIncidents, error, loading, sortRows, sortDirection, sortedId]);

  const handleShowColumn = e => {
    const name = e.target.value;
    const bool = e.target.checked;
    const newState = { ...showColumn, [name]: bool };
    setShowColumn(newState);
    localStoreData(
      newState,
      `${user?.id}-active-incident-table-column-view`,
      false
    );
  };

  const [setSecondaryLocation] = useMutation(SET_SECONDARY_LOCATION, {
    context: { serializationKey: "incidentQueue" },
  });
  const [setStatus] = useMutation(SET_STATUS, {
    context: { serializationKey: "incidentQueue" },
  });
  const [setLastContacted] = useMutation(SET_LAST_CONTACTED, {
    context: { serializationKey: "incidentQueue" },
  });
  const [setOfficerIncidentTime] = useMutation(SET_OFFICER_INCIDENT_TIMES, {
    context: { serializationKey: "incidentQueue" },
  });
  const [setAvailOfficer] = useMutation(SET_AVAIL_OFFICER, {
    context: { serializationKey: "incidentQueue" },
  });

  const windowSize = useWindowSize();

  const handleSecondaryLocationUpdate = (location, oisId, incidentId) => {
    const localDate = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
    let input = {
      originatorId: user?.id,
      officerIncidentSpecificId: oisId,
      secondaryAddress: location,
    };
    activeIncidentDispatch({
      type: UPDATE_OIS,
      payload: {
        incidentId: incidentId,
        oisId: oisId,
        property: "secondaryAddress",
        value: location,
      },
    });
    activeIncidentDispatch({
      type: UPDATE_DEPT_ATTRB,
      payload: {
        incidentId: incidentId,
        oisId: oisId,
        property: "lastContactedAt",
        value: localDate,
      },
    });
    setSecondaryLocation({
      variables: { input },
    });
    setSpecificRowId("");
    setSelectedCell("");
  };

  const handleSetLastContactedAt = (officerId, row) => {
    const allowedToEdit = canEditAvailableOfficers(
      user,
      row?.ori?.id,
      row?.officerId
    );
    if (allowedToEdit) {
      const serverTimeFormat = moment.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      setLastContacted({
        variables: {
          input: {
            officerId: officerId,
            lastContactedAt: serverTimeFormat,
          },
        },
      });
      activeIncidentDispatch({
        type: UPDATE_DEPT_ATTRB,
        payload: {
          incidentId: row.incidentId,
          oisId: row.oisId,
          property: "lastContactedAt",
          value: serverTimeFormat,
        },
      });
    } else {
      console.log(
        `You don't have permission to do that. OfficerId: ${officerId}. UserId: ${user?.id}.`
      );
    }
    return null;
  };

  const handleSetAvailOfficer = (e, row) => {
    const name = e.target.name;
    const value = e.target.value;
    const localDate = moment().utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";

    if (!row?.officerId) return;

    setAvailOfficer({
      variables: {
        input: {
          originatorId: user?.id,
          officerId: row?.officerId,
          [name]: value,
        },
      },
    });
    setSelectedCell("");
    activeIncidentDispatch({
      type: UPDATE_DEPT_ATTRB,
      payload: {
        incidentId: row.incidentId,
        oisId: row.oisId,
        property: name,
        value: value,
      },
    });
    activeIncidentDispatch({
      type: UPDATE_DEPT_ATTRB,
      payload: {
        incidentId: row.incidentId,
        oisId: row.oisId,
        property: "lastContactedAt",
        value: localDate,
      },
    });
  };

  const updateTimer = (logCodeId, oisId, incidentId) => {
    const name = logCodes?.find(logCode => logCode.id === logCodeId)
      ? logCodes?.find(logCode => logCode.id === logCodeId).name
      : "undefined";
    const slashIndex = name.indexOf("/");
    const tenCode = name.substring(0, slashIndex);
    const now = moment().format();
    const localDate = moment(now).utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";

    let timerName;
    switch (tenCode) {
      case "D":
        timerName = "dispatchedAt";
        break;
      case "76":
        timerName = "enrouteAt";
        break;
      case "23":
        timerName = "onSceneAt";
        break;
      case "24":
        timerName = "clearedAt";
        break;
      case "89":
        timerName = "enrouteToHospitalAt";
        break;
      case "90":
        timerName = "arrivedAtHospitalAt";
        break;
      default:
        timerName = false;
    }
    if (timerName) {
      //ADD CODE HERE TO HANDLE CHECKING AND SETTING THE PREVIOUS TIMES AS WELL
      setOfficerIncidentTime({
        variables: {
          input: {
            originatorId: user?.id,
            officerIncidentSpecificId: oisId,
            [timerName]: now,
          },
        },
      });
      timerName === "onSceneAt" &&
        activeIncidentDispatch({
          type: UPDATE_OIS,
          payload: {
            incidentId: incidentId,
            oisId: oisId,
            property: "onSceneAt",
            value: localDate,
          },
        });
      //if timerName === "onSceneAt" then dispatch here to update the onSceneAt
    }
  };

  const LOG_CODE_TO_STATUS = {
    D: "dispatchedAt",
    76: "enrouteAt",
    23: "onSceneAt",
    24: "clearedAt",
    S: "S/Secondary Location",
    89: "89/Enroute to Hospital",
    90: "90/At Hospital",
  };
  const handleStatusChange = (e, row) => {
    const { ois, officer, incidentId } = row || {};
    const logCode = e.target.value;
    const { id: oisId } = ois || {};
    const slashIndex = logCode?.name?.indexOf("/");
    const tenCode = logCode?.name?.substring(0, slashIndex);
    const momentNow = moment().utc();
    const localDate = momentNow.format("YYYY-MM-DD HH:mm:ss") + " UTC";

    updateOfficerTimesInOis(
      ois,
      LOG_CODE_TO_STATUS[tenCode],
      momentNow,
      officer,
      logCodes,
      incidentDispatch,
      activeIncidentDispatch,
      incidentId,
      setStatus,
      user?.id,
      setOfficerIncidentTime,
      null
    );

    updateTimer(logCode?.id, oisId, incidentId);
    handleSetLastContactedAt(officer?.id, row);
    if (logCode.name === "24/Cleared Call") {
      activeIncidentDispatch({
        type: UPDATE_OIS,
        payload: {
          incidentId: incidentId,
          oisId: oisId,
          property: "clearedAt",
          value: localDate,
        },
      });
      // use this to clear the ofc location
      setAvailOfficer({
        variables: {
          input: {
            originatorId: user?.id,
            officerId: officer?.id,
            secondaryAddress: "",
          },
        },
      });
      availableOfficerDispatch({
        type: UPDATE_AVAILABLE_OFFICER,
        payload: {
          officerId: user?.id,
          property: "officerLocation",
          value: "",
        },
      });
    }
  };

  const sortLabel = useRef();

  const handleSort = (event, columnName, id, sortLabel) => {
    const currentDirection = sortDirection;
    let newDirection = null;

    if (currentDirection === "asc") {
      setSortDirection("desc");
      setSortedColumn(columnName);
      setSortedId(id);
      newDirection = "desc";
    } else if (currentDirection === "desc") {
      setSortDirection(null);
      setSortedColumn(null);
      setSortedId(null);
      id = null;
      sortLabel = null;
      columnName = null;
    } else {
      setSortDirection("asc");
      setSortedColumn(columnName);
      setSortedId(id);
      newDirection = "asc";
    }
    sortRows(newDirection, id, sortLabel);
    let storedTableSort = localGetData(`${user?.id}-table-sorting`) || {};
    storedTableSort[TABLE_NAME] = {
      direction: newDirection,
      id: id,
      sortedColumn: columnName,
    };
    localStoreData(storedTableSort, `${user?.id}-table-sorting`);
  };

  function createData(
    oisId,
    incidentNumber,
    badge,
    assignment,
    loanerOriAbbreviations,
    type,
    location,
    secondaryAddress,
    status,
    statusId,
    carNumber,
    radioNumber,
    officerId,
    incidentId,
    logCodeId,
    logCode,
    officerStatus,
    onSceneAt,
    lastContactedAt,
    callTime,
    deptAttrId,
    ori,
    child,
    parent,
    callTypeColor,
    locationColor,
    primaryOfficer,
    disabled,
    ois,
    officer,
    incidentOriId
  ) {
    return {
      oisId,
      incidentNumber,
      badge,
      assignment,
      loanerOriAbbreviations,
      type,
      location,
      secondaryAddress,
      status,
      statusId,
      carNumber,
      radioNumber,
      officerId,
      incidentId,
      logCodeId,
      logCode,
      officerStatus,
      onSceneAt,
      lastContactedAt,
      callTime,
      deptAttrId,
      ori,
      child,
      parent,
      callTypeColor,
      locationColor,
      primaryOfficer,
      disabled,
      ois,
      officer,
      incidentOriId,
    };
  }

  const oriOnTop = (arr, oriId) => {
    let result = [];
    arr.map(i =>
      i?.ori?.id?.toString() === oriId.toString()
        ? result.unshift(i)
        : result.push(i)
    );
    return result;
  };

  const activeIncidentHeaders = [
    {
      title: "Incident\xa0#",
      idName: "incidentNumber",
      show: showColumn.incidentNumber,
    },
    { title: "Badge", idName: "badge", show: showColumn.badge },
    { title: "Assign/Loan", idName: "assignment", show: showColumn.assignment },
    { title: "Type", idName: "type", show: showColumn.type },
    { title: "Location", idName: "location", show: showColumn.location },
    {
      title: "Ofc Status",
      idName: "officerStatus",
      show: showColumn.officerStatus,
    },
    {
      title: "Secondary",
      idName: "secondaryAddress",
      show: showColumn.secondaryAddress,
    },
    { title: "Inc Status", idName: "status", show: showColumn.status },
    { title: "On Scene", idName: "onSceneAt", show: showColumn.onSceneAt },
    {
      title: "Ofc. Timer",
      idName: "officerTimer",
      show: showColumn.officerTimer,
    },
    { title: "Call Timer", idName: "callTimer", show: showColumn.callTimer },
    { title: "Car #", idName: "carNumber", show: showColumn.carNumber },
    { title: "Radio #", idName: "radioNumber", show: showColumn.radioNumber },
  ];
  const [cookies] = useCookies();

  const [tableHeight, setTableHeight] = useLocalStorage(
    `${user?.id}-active-incident-table-height`,
    windowSize.height * 0.45
  );

  if (loading && !activeIncidentsCalled && onlineState)
    return <p>Loading Incidents</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <>
      <div
        className={
          cookies[`mp-userId-${user?.id}-night-mode`] === "true"
            ? classes.tableH3ContainerDark
            : classes.tableH3ContainerLight
        }
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <h3
            data-cy="active-incident-title"
            className={
              cookies[`mp-userId-${user?.id}-night-mode`] === "true"
                ? classes.tableH3TitleDark
                : classes.tableH3TitleLight
            }
          >
            Active Incidents
          </h3>
          {isSuperUser(user?.roleNames) &&
            (import.meta.env.VITE_API_HOST.includes("local") ||
              import.meta.env.VITE_API_HOST.includes("testing")) && (
              <button onClick={cancelAllActiveIncidents}>
                Cancel All Incidents
              </button>
            )}
          &nbsp;&nbsp;
          <HelpIconButton objectname="mobile_active_incidents" />
          {/* {import.meta.env.DEV && <button onClick={cancelAllActiveIncidents}>Cancel All Incidents</button>} */}
          {/* Refetch is for testing purposes */}
          {/* <Button onClick={() => refetch()}>Refetch</Button> */}
          <TableColumnShowHide
            headers={activeIncidentHeaders}
            handleShowColumn={handleShowColumn}
          />
        </Box>
      </div>
      <ResizableBox
        width={dashSize.width}
        // height={windowSize.height * .45}
        height={tableHeight}
        handleSize={[20, 20]}
        handle={<DragHandleIcon className={`react-resizable-handle`} />}
        axis={"y"}
        onResizeStop={(event, { size }) => {
          setTableHeight(size.height);
        }}
      >
        <TableContainer className={classes.tableContainer} component={Paper}>
          <Table
            data-cy="active-incident-table"
            stickyHeader
            className={classes.table}
            aria-label="simple table"
          >
            <TableHeaders
              headers={activeIncidentHeaders}
              showColumn={showColumn}
              handleSort={handleSort}
              sortLabel={sortLabel}
              sortedColumn={sortedColumn}
              sortDirection={sortDirection}
            />
            <ActiveIncidentTableRows
              rows={rows}
              setSpecificRowId={setSpecificRowId}
              specificRowId={specificRowId}
              handleSecondaryLocationUpdate={handleSecondaryLocationUpdate}
              handleStatusChange={handleStatusChange}
              handleSetLastContactedAt={handleSetLastContactedAt}
              selectedCell={selectedCell}
              setSelectedCell={setSelectedCell}
              handleSetAvailOfficer={handleSetAvailOfficer}
              showColumn={showColumn}
              handleIncidentOpening={handleIncidentOpening}
              userPreferences={userPreferences}
            />
          </Table>
        </TableContainer>
      </ResizableBox>
    </>
  );
};

export default ActiveIncidentTableContainer;
